import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CloseOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { Button, Flex, Form, Select, Typography, message } from 'antd';
import { setWorkflow, setSelectedStep } from '../../../redux/slices/connectorSlice';
import UserRoleDropdown from '../../UserRoleDropdown';
import { useTranslation } from 'react-i18next';

const VERIFICATION_TITLES = {
  sharePublicForm: 'Share Public Form'
};

const VerificationPanel = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { selectedStep, workflow } = useSelector(state => state.connector);
  const { userInfo } = useSelector(state => state.user);
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);

  const type = selectedStep?.data?.type || selectedStep?.type;
  const title = VERIFICATION_TITLES[type];
  const isVerification = !!(selectedStep?.id && title);
  const isSharePublicForm = type === 'sharePublicForm';
  const nodeId = selectedStep?.id;

  useEffect(() => {
    if (!isVerification || !nodeId) return;
    const node = (workflow.nodes || []).find(n => n.id === nodeId);
    const dv = node?.data || {};
    form.setFieldsValue({
      name: dv?.name || title,
      assignmentType: dv?.assignmentType,
      spaceToBeLinked: dv?.spaceToBeLinked,
      shareFormId: dv?.shareFormId,
      publicLinkRecipients: dv?.publicLinkRecipients || { user: [], role: [], group: [], userPicker: [] }
    });
  }, [isVerification, nodeId, workflow.nodes, form, title]);

  useEffect(() => {
    const el = document.getElementById('verification-panel');
    if (el) el.style.display = isVerification ? 'block' : 'none';
  }, [isVerification]);

  const onClose = () => dispatch(setSelectedStep({}));

  const onSave = async (values) => {
    try {
      const nextNodes = (workflow.nodes || []).map(n => {
        if (n.id !== nodeId) return { ...n };
        return {
          ...n,
          data: {
            ...n.data,
            type,
            name: values.name || title,
            assignmentType: values.assignmentType,
            spaceToBeLinked: values.spaceToBeLinked,
            shareFormId: values.shareFormId,
            publicLinkRecipients: values.publicLinkRecipients
          },
        };
      });
      dispatch(setWorkflow({ ...workflow, nodes: nextNodes }));
      onClose();
    } catch (e) {
      message.error(t('Please complete the form'));
    }
  };

  if (!isVerification) return null;

  const spaceOptions = (userInfo?.spaceInfo || []).map(space => ({
    label: space?.name || space?.spaceName || space?.spaceId,
    value: space?.spaceId
  }));

  return (
    <div className="step-details-container" id="verification-panel">
      <Flex className='step-details-header' align='center' justify='space-between'>
        <Typography.Title level={5} className='margin-0'>
          <Flex gap={8} align='center'>
            <SafetyCertificateOutlined />
            {t(title)}
          </Flex>
        </Typography.Title>
        <CloseOutlined className='icon-medium' onClick={onClose} />
      </Flex>
      <div className='step-details-body'>
        <Form form={form} layout='vertical' onFinish={onSave}>
          {isSharePublicForm && (
            <>
              <Form.Item label={t('Space')} name='spaceToBeLinked' rules={[{ required: true, message: 'Please select a space' }]}>
                <Select
                  placeholder={t('Choose a space')}
                  options={spaceOptions}
                  showSearch
                  optionFilterProp='label'
                  filterOption={(input, option) => (option?.label || '').toLowerCase().includes((input || '').toLowerCase())}
                  onChange={() => form.setFieldValue('shareFormId', undefined)}
                />
              </Form.Item>
              <Form.Item label={t('Public Link Recipients')} name='publicLinkRecipients'>
                <UserRoleDropdown
                  selectedOptions={formValues?.publicLinkRecipients || { user: [], role: [], group: [], userPicker: [] }}
                  onSelect={(values) => form.setFieldValue('publicLinkRecipients', {
                    user: values.user || [],
                    role: values.role || [],
                    group: values.group || [],
                    userPicker: values.userPicker || []
                  })}
                  tabsRequired={["user", "group", "role", "userPicker"]}
                  getSpaceUsers={true}
                />
              </Form.Item>
            </>
          )}

          <Form.Item className='step-details-action'>
            <Flex gap={12}>
              <Button type='primary' htmlType='submit' className='submit-button' style={{ width: '9.75rem' }}>{t('Save')}</Button>
              <Button onClick={onClose} style={{ width: '9.75rem' }}>{t('Cancel')}</Button>
            </Flex>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default VerificationPanel;
